import React from "react";

import { createSelector } from "@reduxjs/toolkit";
import { INSURANCE_TYPE_OPTIONS, SUPPORTED_CARRIERS, SUPPORTED_LIFE_CARRIERS, SUPPORTED_DISABILITY_CARRIERS } from "BrokerOfRecord/constants";
import { useGetUserExperienceFlagsQuery } from "EmployerApplication/api";
import { useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { reduxForm, formValueSelector, Field } from "redux-form";
import focusFirstError from "Utils/focusFirstError";
import { validateRequired } from "Utils/Validations";

import { useUpdateApplicationMutation, useUpdateNetsuitedDeductionAccountsMutation, useGetApplicationQuery, getBorApplication } from "../api";
import BORPage from "../components/BORPage";
import {
  DependentPercentage,
  EmployeePercentage,
  DependentFixedAmount,
  EmployeeFixedAmount,
  OtherContributionInformation,
} from "../components/CommonQuestions";
import { baseURL, fileUploadURL, confirmationURL } from "../urls";
import { H5, Title } from "Cobalt/Header";
import AppearingElement from "Common/AppearingElement";
import Button from "Common/Button";
import { SIFormField, SIFormRadioGroup, SIFormSelect } from "Common/forms";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";
import { usePartnerContext } from "Common/PartnerContext";

import makeBem from "Utils/makeBem";

const bem = makeBem("BORMainContent");

const CONTRIBUTION_FORM = "contribution";

const getTypeLabel = (planType: string) =>
  INSURANCE_TYPE_OPTIONS.find((o) => o.value === planType)?.label ??
  // graceful fallback if a type isn’t in the list
  planType.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

const withReduxForm = reduxForm({
  form: CONTRIBUTION_FORM,
  destroyOnUnmount: false,
  enableReinitialize: true,
  // inside withReduxForm
  onSubmit: async (values, _dispatch, props) => {
    const { companyContributions, applyToAll, ...rest } = values;

    const normalizedContributions: Record<string, any[]> = {};
    Object.entries(companyContributions || {}).forEach(([planType, plans]) => {
      if (applyToAll?.[planType]) {
        const first = (plans as any[])?.[0];
        normalizedContributions[planType] = (plans as any[]).map((plan) => ({
          // keep everything already collected for this plan (including id)
          ...plan,
          contributions: {
            typeOfContribution: first?.typeOfContribution,
            employee: first?.employee,
            dependent: first?.dependent,
          },
        }));
      } else {
        // keep everything already collected (including id) but nest contribution fields
        normalizedContributions[planType] = (plans as any[]).map((plan) => ({
          ...plan,
          contributions: {
            typeOfContribution: plan?.typeOfContribution,
            employee: plan?.employee,
            dependent: plan?.dependent,
          },
        }));
      }
    });

    const payload = {
      borApplicationIntake: {
        plans: normalizedContributions,
        documents: props.initialValues.documents || {},
      },
      ...rest,
    };

    await props.updateBorApplication(payload).unwrap();
  },
  onSubmitFail: (errors) => {
    focusFirstError(errors, ".");
  },
  onSubmitSuccess: (_values, _dispatch, { history }) => {
    history.push(`${baseURL}${fileUploadURL}`);
  },
});

const AcaQuestions = ({ acaEmployeeData }) => {
  const formSelectorLocal = formValueSelector(CONTRIBUTION_FORM);
  const coveringPartTime = useSelector((state) => formSelectorLocal(state, "coveringPartTime"));
  const isLargeGroup = useSelector((state) => formSelectorLocal(state, "isLargeGroup"));
  const { totalCount, fullTimeCount, partTimeCount, largeGroupQualifyingNumber } = acaEmployeeData || {};

  const hideIsLargeGroup = coveringPartTime == "Yes";
  const hideMeasurementMethod = hideIsLargeGroup || isLargeGroup != "true";
  return (
    <Row pt={4}>
      <SIFormField
        name="coveringPartTime"
        label="Offer insurance to part-time employees?"
        component={SIFormRadioGroup}
        className={bem("form", "radioGroup")}
        guidance="Most companies choose no."
        props={{
          options: {
            Yes: "Yes",
            No: "No",
          },
        }}
        validate={validateRequired}
      />
      <SIFormField
        name="isLargeGroup"
        label={
          !hideIsLargeGroup ? (
            `We detect ${totalCount} employees on payroll (${fullTimeCount} full-time, ${partTimeCount} part-time). Are more than ${largeGroupQualifyingNumber} employees eligible for benefits?`
          ) : (
            <>
              <br />
              <br />
            </>
          )
        }
        component={SIFormRadioGroup}
        className={bem("form", "radioGroup")}
        style={{ visibility: hideIsLargeGroup ? "collapse" : "visible" }}
        props={{
          disabled: hideIsLargeGroup,
          options: {
            true: "Yes",
            false: "No",
          },
        }}
        validate={validateRequired}
      />
      <SIFormField
        name="measurementMethod"
        label={!hideMeasurementMethod ? "How should we measure part-time employees for eligibility?" : <br />}
        component={SIFormRadioGroup}
        className={bem("form", "radioGroup")}
        style={{ visibility: hideMeasurementMethod ? "collapse" : "visible" }}
        guidance={
          !hideMeasurementMethod && (
            <>
              <p>Look-Back Measurement Method is recommended for companies with variable-hour or seasonal employees.</p>
              <p>Monthly Measurement Method is recommended for companies with a stable workforce.</p>
            </>
          )
        }
        props={{
          disabled: hideMeasurementMethod,
          options: {
            lookback: "Look-Back 12 Months",
            monthly: "Monthly Review",
          },
        }}
        validate={hideMeasurementMethod ? "" : validateRequired}
      />
    </Row>
  );
};

const carrierName = ({ plan }) => {
  if (plan.insuranceCompany) {
    return Object.fromEntries([...SUPPORTED_CARRIERS, ...SUPPORTED_LIFE_CARRIERS, ...SUPPORTED_DISABILITY_CARRIERS])[plan.insuranceCompany];
  }

  return plan.otherInsuranceCompany;
};

// --- Contribution fields per plan ---
const PlanContributionFields = ({ plan, fieldPrefix, isFirstPlan, planType }) => {
  const formSelectorLocal = formValueSelector(CONTRIBUTION_FORM);
  const contributionType = useSelector((state) => formSelectorLocal(state, `${fieldPrefix}.typeOfContribution`));
  const applyToAll = useSelector((state) => formSelectorLocal(state, `applyToAll.${planType}`));

  return (
    <div className={`BORPlanRow-${planType} mb-6 pl-4 border-l`} key={plan.id || plan.planName}>
      <Row>
        <Col sm={3}>
          {carrierName({ plan })} - {plan.planName}
        </Col>
        <Col sm={6}>
          <SIFormField
            name={`${fieldPrefix}.typeOfContribution`}
            label="Contribution Type"
            component={SIFormSelect}
            className="p-0"
            props={{
              placeholder: "Select...",
              disabled: !isFirstPlan && applyToAll,
              options: {
                "% of the least expensive plan": "percent_of_base_plan",
                "% of total premium": "percentage",
                "Flat dollar amount": "fixed",
                "Class-based": "class_based",
                Other: "other",
              },
            }}
            validate={!isFirstPlan && applyToAll ? undefined : validateRequired}
          />

          <AppearingElement show={isFirstPlan || (!isFirstPlan && !applyToAll)}>
            <AppearingElement show={contributionType === "percentage" || contributionType === "percent_of_base_plan"}>
              <Row>
                <EmployeePercentage namePrefix={`${fieldPrefix}.employee`} />
                <DependentPercentage namePrefix={`${fieldPrefix}.dependent`} />
              </Row>
            </AppearingElement>

            <AppearingElement show={contributionType === "fixed"}>
              <Row>
                <EmployeeFixedAmount namePrefix={`${fieldPrefix}.employee`} />
                <DependentFixedAmount namePrefix={`${fieldPrefix}.dependent`} />
              </Row>
            </AppearingElement>
          </AppearingElement>
        </Col>

        {isFirstPlan && (
          <Col sm={3} className="text-right">
            <label htmlFor={`applyToAll-${planType}`} className="ml-2 text-sm">
              Use for all {getTypeLabel(planType)} plans?
            </label>
            <Field name={`applyToAll.${planType}`} id={`applyToAll-${planType}`} component="input" type="checkbox" />
          </Col>
        )}
      </Row>
    </div>
  );
};

const ContributionsForPlans = ({ planType, plans }) => {
  const formSelectorLocal = formValueSelector(CONTRIBUTION_FORM);
  const applyToAll = useSelector((state) => formSelectorLocal(state, `applyToAll.${planType}`));

  return (
    <div className="mb-8">
      <H5 className="mb-2">{getTypeLabel(planType)} Plans</H5>
      {plans.map((plan, planIndex) => {
        const fieldPrefix = `companyContributions.${planType}[${planIndex}]`;
        return (
          <PlanContributionFields
            key={plan.id || plan.planName || `${planType}-${planIndex}`}
            plan={plan}
            fieldPrefix={fieldPrefix}
            planType={planType}
            isFirstPlan={planIndex === 0}
          />
        );
      })}
    </div>
  );
};

const planOrder = ["medical", "dental", "vision", "termLife", "wholeLife", "shortTermDisability", "longTermDisability"];

const Contributions = ({ intakePlans }) => {
  return (
    <>
      {planOrder.map((planType) => {
        const plans = intakePlans[planType];
        if (!plans || !plans.length) return null;
        return <ContributionsForPlans key={planType} planType={planType} plans={plans} />;
      })}
    </>
  );
};

const ContributionAmountSectionForm = withReduxForm(
  ({ handleSubmit, submitting, showOtherContributionSection, showAcaQuestions, acaEmployeeData, initialValues: { netsuiteSelectOptions }, intakePlans }) => (
    <form className={bem("form")} onSubmit={handleSubmit}>
      <Row>
        <Col sm={8} mb={3}>
          <SIFormField
            name="probationaryPeriod"
            label="Waiting Period For New Employees"
            component={SIFormSelect}
            props={{
              options: {
                "1st of month after hire date": "1st of month after hire date",
                "1 month": "1 month",
                "2 months": "2 months",
              },
            }}
            guidance="Choose the amount of time new employees must wait before they are eligible for insurance benefits."
            validate={validateRequired}
            noBounce
          />
        </Col>
      </Row>

      <hr className={bem("divider")} />
      <Row>
        <Col mb={3}>
          <Title>Enter the amount that you currently contribute to your employees insurance.</Title>
        </Col>
      </Row>

      {/* Contributions grouped by plan type */}
      <Contributions intakePlans={intakePlans} />

      {showAcaQuestions && <AcaQuestions acaEmployeeData={acaEmployeeData} />}
      <AppearingElement show={showOtherContributionSection}>
        <Row>
          <OtherContributionInformation />
        </Row>
      </AppearingElement>
      <Row>
        <Col smOffset={8} mdOffset={9}>
          <Button fullWidth submitting={submitting} disabled={submitting} size="medium" type="submit">
            Next
          </Button>
        </Col>
      </Row>
    </form>
  ),
);

// --- initialValues builder ---
const formInitialValues = createSelector(getBorApplication, (borApplication) => {
  const {
    contributionMethod,
    netsuiteSelectOptions,
    coveringPartTime,
    isLargeGroup,
    measurementMethod,
    partnerCompanyPreferenses: initPreferences,
    borApplicationIntake,
    probationaryPeriod,
  } = borApplication || {};

  const preferences = {
    expense_account_id: initPreferences?.expenseAccountID,
    liability_account_id: initPreferences?.liabilityAccountID,
  };

  const companyContributions: Record<string, any[]> = {};
  const intakePlans = borApplicationIntake?.plans || {};

  // For each plan in intake, copy through id + planName so we can render stable keys
  Object.entries(intakePlans).forEach(([planType, plans]) => {
    companyContributions[planType] = (plans as any[]).map((plan) => ({
      // keep the plan id (may be undefined for legacy; still safe)
      id: plan.id || null,
      planName: plan.planName || null,
      groupIDNumber: plan.groupIDNumber || null,
      insuranceCompany: plan.insuranceCompany || null,
      otherInsuranceCompany: plan.otherInsuranceCompany || null,
      // pre-seed contribution fields (if any were saved earlier)
      typeOfContribution: plan.contributions?.typeOfContribution || null,
      employee: plan.contributions?.employee || null,
      dependent: plan.contributions?.dependent || null,
    }));
  });

  return {
    contributionMethod,
    companyContributions,
    netsuiteSelectOptions,
    preferences,
    coveringPartTime,
    isLargeGroup,
    measurementMethod,
    probationaryPeriod,
    applyToAll: {}, // initialize applyToAll checkboxes
    documents: borApplicationIntake?.documents || {},
  };
});

const ContributionPage = () => {
  const [updateBorApplication] = useUpdateApplicationMutation();
  const [updateNetsuitedDeductionAccounts] = useUpdateNetsuitedDeductionAccountsMutation();
  const history = useHistory();
  const initialValues = useSelector(formInitialValues);
  const { partner } = usePartnerContext();
  const { data: borApplication } = useGetApplicationQuery();
  const { data: userExperienceFlags } = useGetUserExperienceFlagsQuery({ companyIDToken: borApplication?.idToken });
  const showAcaQuestions = !!borApplication?.showAcaQuestions;
  const acaEmployeeData = borApplication?.acaEmployeeData;
  const intakePlans = borApplication?.borApplicationIntake?.plans || {};

  return (
    <BORPage>
      <Button component={Link} size="medium" to={`${baseURL}${confirmationURL}`} color="link" className={bem("backButton")} pl={0}>
        <span>
          <Icon icon="chevron-left" pr={3} />
          Back
        </span>
      </Button>
      <H5 className={bem("title")}>Contributions and Probationary Period</H5>
      <hr className={bem("divider")} />
      <ContributionAmountSectionForm
        updateBorApplication={updateBorApplication}
        updateNetsuitedDeductionAccounts={updateNetsuitedDeductionAccounts}
        showOtherContributionSection={userExperienceFlags?.has_ancilaries || false}
        partner={partner}
        history={history}
        showAcaQuestions={showAcaQuestions}
        acaEmployeeData={acaEmployeeData}
        initialValues={initialValues}
        intakePlans={intakePlans}
      />
    </BORPage>
  );
};

export default ContributionPage;
